import React, { useRef, useEffect, useState, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import noUiSlider from 'nouislider';
import { FormattedMessage } from 'react-intl';
import { IEvent, attachGaDataAttributes } from '../../Plans/analytics';
import { FlatsFiltersContentContext } from '../FlatsFiltersContext';
import {
  formatNumberFrom,
  formatNumberTo,
  getValuesLabel,
  scaleToPrice,
} from '../../Funnel/FunnelPrice/rangeHelper';

const FlatsFiltersRange = ({
  type,
  event,
  rangeMin,
  rangeMax,
  minValue,
  maxValue,
  range,
  isPriceFilter,
  onCurrencyChange,
}) => {
  const { searchParams, updateSearchParams, currency } = useContext(FlatsFiltersContentContext);
  const [currentValues, setCurrentValues] = useState({ min: minValue, max: maxValue });
  const [rangeText, setRangeText] = useState('');
  const [currentParams, setCurrentParams] = useState({
    min: searchParams[`min_${type}`],
    max: searchParams[`max_${type}`],
  });
  const sliderRef = useRef(null);
  const isTooSmallRange = useMemo(() => {
    if (!range || !range.min || !Array.isArray(range.min)) return false;
    const [min, step] = range.min;

    return range.max - step < min;
  }, [range]);

  const getRangeValue = (values) => {
    if (values[0] === values[1]) {
      return values[0];
    }

    return `${values[0]} — ${values[1]}`;
  };

  const getFormattedValues = (values) => {
    return isPriceFilter
      ? scaleToPrice(values, rangeMin, rangeMax)
      : values.map((value) => Math.round(value));
  };

  const onChange = useCallback(
    (values) => {
      const formattedValues = getFormattedValues(values);
      let paramsValues;
      if (isPriceFilter && currency === 'usd') {
        paramsValues = formattedValues.map((v) => Math.round(v * window.currencyRateUSD));
      } else {
        paramsValues = formattedValues;
      }

      if (formattedValues[0] !== rangeMin && formattedValues[1] !== rangeMax) {
        setCurrentParams({ min: paramsValues[0], max: paramsValues[1] });
      } else if (formattedValues[0] !== rangeMin && formattedValues[1] === rangeMax) {
        setCurrentParams({ min: paramsValues[0], max: null });
      } else if (formattedValues[0] === rangeMin && formattedValues[1] !== rangeMax) {
        setCurrentParams({ min: null, max: paramsValues[1] });
      } else {
        setCurrentParams({ min: null, max: null });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currency, rangeMin, rangeMax],
  );

  useEffect(() => {
    if (rangeMin === rangeMax || isTooSmallRange) return;

    noUiSlider.create(sliderRef.current, {
      start: [currentValues.min, currentValues.max],
      connect: true,
      range: range || { min: rangeMin, max: rangeMax },
      step: 1,
      format: isPriceFilter
        ? {
            from: (value) => formatNumberFrom(value, rangeMin, rangeMax),
            to: formatNumberTo,
          }
        : {
            from: (value) => Math.round(value),
            to: (value) => Math.round(value),
          },
    });

    sliderRef.current.noUiSlider.on('update', (values) => {
      const formattedValues = getFormattedValues(values);
      if (formattedValues[0] !== rangeMin && formattedValues[1] !== rangeMax) {
        setCurrentValues({ min: formattedValues[0], max: formattedValues[1] });
      } else if (formattedValues[0] !== rangeMin && formattedValues[1] === rangeMax) {
        setCurrentValues({ min: formattedValues[0], max: rangeMax });
      } else if (formattedValues[0] === rangeMin && formattedValues[1] !== rangeMax) {
        setCurrentValues({ min: rangeMin, max: formattedValues[1] });
      } else {
        setCurrentValues({ min: rangeMin, max: rangeMax });
      }
    });

    sliderRef.current.noUiSlider.on('change', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sliderRef.current || !sliderRef.current.noUiSlider) return;
    sliderRef.current.noUiSlider.off('change');
    sliderRef.current.noUiSlider.on('change', onChange);
  }, [onChange]);

  useEffect(() => {
    const updateMin = currentParams.min !== searchParams[`min_${type}`];
    const updateMax = currentParams.max !== searchParams[`max_${type}`];

    if (updateMin || updateMax) {
      updateSearchParams({
        ...searchParams,
        ...(updateMin && { [`min_${type}`]: currentParams.min }),
        ...(updateMax && { [`max_${type}`]: currentParams.max }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentParams]);

  useEffect(() => {
    if (!sliderRef.current || !sliderRef.current.noUiSlider) return;
    sliderRef.current.noUiSlider.updateOptions(
      {
        start: [minValue, maxValue],
        range,
      },
      false,
    );
  }, [minValue, maxValue, range]);

  useEffect(() => {
    const labels = isPriceFilter
      ? getValuesLabel(
          [formatNumberTo(currentValues.min), formatNumberTo(currentValues.max)],
          rangeMin,
          rangeMax,
          currency,
        )
      : currentValues;

    setRangeText(getRangeValue([labels.min, labels.max]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValues, currency]);

  useEffect(() => {
    if (!isPriceFilter || !sliderRef.current || !sliderRef.current.noUiSlider) return;
    const values = sliderRef.current.noUiSlider.get();
    if (onCurrencyChange) {
      onCurrencyChange(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency]);

  if (rangeMin === rangeMax || isTooSmallRange) {
    return null;
  }

  return (
    <div className="FlatsFiltersRange">
      <div className="Slider -outlined" {...attachGaDataAttributes(event)}>
        <div className="Slider-label">
          <FormattedMessage id={type} />
        </div>
        <div ref={sliderRef} />
        <div className="Slider-value">{rangeText}</div>
      </div>
    </div>
  );
};

FlatsFiltersRange.propTypes = {
  type: PropTypes.string.isRequired,
  event: PropTypes.shape(IEvent).isRequired,
  rangeMin: PropTypes.number.isRequired,
  rangeMax: PropTypes.number.isRequired,
  minValue: PropTypes.number.isRequired,
  maxValue: PropTypes.number.isRequired,
  range: PropTypes.object,
  isPriceFilter: PropTypes.bool,
  onCurrencyChange: PropTypes.func,
};

export default FlatsFiltersRange;
